/**
 * Vellatini pre-order variant-switch sync (spec-14 section 4.4 step 4, the
 * known Horizon-shaped morph gap: "Hidden inputs are not in product-form's
 * morph whitelist").
 *
 * assets/product-form.js's own #onProductSelect morphs a fixed whitelist of
 * refs from the fetched section HTML (`[ref="addToCartButton"]`, quantity
 * label/rules, price-per-item, volume-pricing - see the `morphTargets` array
 * in that file). The pre-order props wrapper (`[data-preorder-props]`) and
 * the PDP pre-order card (`[data-preorder-block]`) are theme-owned additions
 * outside that list, so without this module they would go stale on a
 * variant switch: a shopper who selects a variant with a DIFFERENT batch
 * would still add-to-cart with the PREVIOUS variant's batch code/eta
 * properties, and the PDP card would keep showing the old batch.
 *
 * Fix, event-driven (spec-14 section 4.4 step 4, option 1 - "JS sync"):
 * listen on the same bubbling `shopify:product:select` event
 * (`StandardEvents.productSelect`, assets/standard-events.d.ts) that
 * <variant-picker> dispatches on itself after a variant change resolves.
 * The event carries a deferred `event.promise` that resolves with
 * `{ variant, detail: { html, ... } }`, where `detail.html` is the freshly
 * fetched, already-parsed section document for the NEW variant - the exact
 * same document product-form.js mines for its own whitelist. This module
 * copies two additional wrappers out of that document instead of extending
 * Horizon's own product-form.js (kept out of core theme JS on purpose - see
 * spec-14 section 4.4 step 4's second, file-editing option, not taken here).
 *
 * No cart mutation, no metafield write: purely re-rendering theme-owned
 * markup from data the server already computed for the new variant.
 *
 * M2 code-review fix: a section can hold MORE than one of each wrapper at
 * once (the buy box's own `[data-preorder-props]`/`[data-preorder-block]`
 * plus one more `[data-preorder-props]` per attach-row mini-add rendered in
 * the same section - see snippets/vellatini-mini-add.liquid). The event
 * target's closest section is the scope root for ALL of them, so a plain
 * `querySelector` (first DOM match) could silently copy the WRONG product's
 * markup into the wrong wrapper whenever the buy box isn't first in source
 * order. Every wrapper already carries a unique id (`PreorderProps-*`,
 * `Preorder-*`, `PreorderData-*` - section_id/id_prefix/product_id keyed,
 * see snippets/preorder-props.liquid, preorder-block.liquid, preorder-
 * data.liquid), so this module pairs live and fetched wrappers by id instead
 * of DOM position.
 */

const SYNC_SELECTORS = ['[data-preorder-props]', '[data-preorder-block]'];
const DATA_SELECTOR = '[data-vell-preorder-data]';

/**
 * Pairs every live wrapper matching `selector` inside `scopeRoot` with its
 * counterpart in the freshly fetched `html` document by shared `id`, and
 * runs `apply(current, next)` for each matched pair. Wrappers without an id
 * are skipped entirely - id-less matching is exactly the DOM-order bug this
 * exists to avoid, so silently no-op rather than guess.
 *
 * @param {ParentNode} scopeRoot
 * @param {ParentNode} html
 * @param {string} selector
 * @param {(current: Element, next: Element) => void} apply
 */
function syncWrappersById(scopeRoot, html, selector, apply) {
  const nextById = new Map();
  for (const next of html.querySelectorAll(selector)) {
    if (next.id) nextById.set(next.id, next);
  }
  for (const current of scopeRoot.querySelectorAll(selector)) {
    if (!current.id) continue;
    const next = nextById.get(current.id);
    if (next) apply(current, next);
  }
}

/**
 * @param {Event & { target: EventTarget | null, promise?: Promise<any> }} event
 */
async function handleProductSelect(event) {
  if (typeof event.promise?.then !== 'function') return;

  // Mirror product-form.js's own scoping: a variant-picker inside a card,
  // dialog, or plain section only concerns the nearest one of those - never
  // the whole document (multiple pre-order forms can coexist, e.g. the PDP
  // buy box and a cart-drawer suggest row rendered at the same time).
  const scopeRoot =
    /** @type {Element | null} */ (event.target)?.closest?.('.shopify-section, dialog, product-card') ?? document;

  let detail;
  try {
    ({ detail } = await event.promise);
  } catch {
    // A rejected/aborted variant-change promise leaves the live DOM as-is;
    // product-form.js's own #onProductSelect bails the same way.
    return;
  }

  const html = detail?.html;
  if (!html) return;

  for (const selector of SYNC_SELECTORS) {
    syncWrappersById(scopeRoot, html, selector, (current, next) => {
      current.innerHTML = next.innerHTML;
    });
  }

  // Kept in sync even though no current UI reads it client-side (defensive -
  // any future preorder-aware module can rely on this script tag being
  // correct for the live variant without adding its own fetch).
  syncWrappersById(scopeRoot, html, DATA_SELECTOR, (current, next) => {
    current.textContent = next.textContent;
  });
}

document.addEventListener('shopify:product:select', handleProductSelect);
